import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { BrandSync, isAuthScreen } from '@/components/brand/BrandSync'
import { applyBrand, rememberedBrand } from '@/lib/brand'
import { Toaster } from 'sonner'
import { ConfirmHost } from './components/ui/confirm'

import './index.css'
import LoginPage from './routes/Login'
import Verify2FaPage from './routes/Verify2FA'
import ForgotPasswordPage from './routes/ForgotPassword'
import ResetPasswordPage from './routes/ResetPassword'
import AcceptInvitePage from './routes/AcceptInvite'
import SignupPage from './routes/Signup'
import TermsPage from './routes/Terms'
import PrivacyPage from './routes/Privacy'
import OnboardingPage from './routes/Onboarding'
import { ProfileGate } from './components/onboarding/ProfileGate'
import { ForcePasswordChange } from './components/auth/ForcePasswordChange'
import AppShell from './components/layout/AppShell'
import './styles/glass.css'
import './styles/season.css'
import OverviewPage from './routes/Overview'
import OrdersPage from './routes/Orders'
import OrderCreatePage from './routes/OrderCreate'
import OrderDetailPage from './routes/OrderDetail'
import { ShipPaymentBatchPage, ShipPaymentsPage } from './routes/ShipPayments'
import CustomersPage from './routes/Customers'
import ReconcilePage from './routes/Reconcile'
import ReportsPage from './routes/Reports'
import ProductsPage from './routes/Products'
import ProductCreatePage from './routes/ProductCreate'
import SettingsPage from './routes/Settings'
import ShippingPage from './routes/Shipping'
import FeedbackPage from './routes/Feedback'
import NotificationsPage from './routes/Notifications'
// Đại lý
import AgencyOverview from './routes/agency/AgencyOverview'
import AgencyOrders from './routes/agency/AgencyOrders'
// Cộng tác viên (CTV)
import CtvDashboard from './routes/ctv/CtvDashboard'
import NetworkPage from './routes/network/Network'
import UpgradeRequestsPage from './routes/network/UpgradeRequests'
import NetworkAnnouncementsPage from './routes/network/Announcements'
import UpgradePage from './routes/upgrade/Upgrade'
import CtvOrders from './routes/ctv/CtvOrders'
import PriceListsPage from './routes/PriceLists'
import AgencyCommissionsPage from './routes/commissions/AgencyCommissions'
import CtvCommissionsPage from './routes/commissions/CtvCommissions'
// Gom Đơn (apps/gomdon): trang tải lười, chỉ mở khi engine trả lời /gomdon/api/me cho đơn vị đang mở
import { GomdonPage, GomdonRedirect } from './features/gomdon/GomdonPage'
// Quét mã đăng nhập (docs/KE_HOACH_DANG_NHAP_QR.md) — nạp riêng: camera + bộ đọc mã chỉ cần khi mở màn này
const QrScanPage = React.lazy(() => import('./routes/QrScan'))

import { getAuthState, subscribe, tryRestoreSession, homeRouteOf } from './lib/auth'
// Khu Quản trị NPP (docs/KE_HOACH_NPP_SANG_WEB.md)
import { NppGate } from './npp/NppGate'
import { nppRoutes } from './npp/routes'
import { hasAccess, getHomeRouteForTenant } from './lib/permissions'
import { useViewportMode } from './hooks/useViewportMode'
import { queryClient } from './lib/queryClient'
import './lib/pwa'

function useAuthState() {
  const [state, setState] = React.useState(getAuthState())
  React.useEffect(() => {
    const unsubscribe = subscribe(setState)
    return () => { unsubscribe() }
  }, [])
  return state
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const s = useAuthState()
  const location = useLocation()
  if (!s.isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />
  }
  if (!s.is2FaVerified) return <Navigate to="/2fa" replace />
  // Nhân viên còn mật khẩu tạm chủ đơn vị đặt → đổi trước khi vào bất cứ trang nào (máy chủ cũng chặn)
  if (s.mustChangePassword) return <ForcePasswordChange />
  return <>{children}</>
}

/**
 * "/" không còn trang giới thiệu: gõ shoptrack.vn là vào thẳng đăng nhập;
 * đã đăng nhập thì vào trang tổng quan đúng vai trò.
 */
function RootRedirect() {
  const s = useAuthState()
  if (!s.isAuthenticated) return <Navigate to="/login" replace />
  if (!s.is2FaVerified) return <Navigate to="/2fa" replace />
  return <Navigate to={homeRouteOf(s, getHomeRouteForTenant)} replace />
}


/**
 * Hoa hồng CTV (GĐ8): Đại lý quản lý bảng ở /commissions, CTV xem của mình ở /commissions/me.
 * Vào nhầm trang của vai trò kia thì chuyển sang trang đúng; Shop không có hoa hồng.
 */
function CommissionsRoute({ ctvView }: { ctvView: boolean }) {
  const s = useAuthState()
  const shop = s.shops.find((x) => x.shopId === s.activeShopId)
  const type = shop?.tenantType || (shop?.role === 'agency' || shop?.role === 'ctv' ? shop.role : 'shop')
  // Đại lý từng là CTV xem lại hoa hồng thời làm CTV ở /commissions/me (máy chủ chặn nếu chưa từng là CTV)
  if (type === 'agency') return ctvView ? <CtvCommissionsPage /> : <AgencyCommissionsPage />
  if (type === 'ctv') return ctvView ? <CtvCommissionsPage /> : <Navigate to="/commissions/me" replace />
  return <Navigate to={getHomeRouteForTenant(shop?.tenantType, shop?.role, shop?.staff, shop?.workModes)} replace />
}

function UnitSwitching() {
  return (
    <div className="grid min-h-[40vh] place-items-center text-muted">
      <div className="flex items-center gap-2 text-sm">
        <svg viewBox="0 0 24 24" className="size-4 animate-spin" aria-hidden>
          <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeDasharray="40 60" />
        </svg>
        Đang chuyển đơn vị…
      </div>
    </div>
  )
}

function RequireRole({ children, path }: { children: React.ReactNode, path: string }) {
  const s = useAuthState()
  const shop = s.shops.find((shop) => shop.shopId === s.activeShopId)
  if (!hasAccess(shop?.role, path, shop?.tenantType, shop?.staff, shop?.workModes)) {
    const fallback = getHomeRouteForTenant(shop?.tenantType, shop?.role, shop?.staff, shop?.workModes)
    if (fallback === path) {
      return <Navigate to="/login" replace />
    }
    return <Navigate to={fallback} replace />
  }
  return <>{children}</>
}


// Giao diện theo mùa admin đang bật — tải song song với khôi phục phiên (trang Đăng nhập cũng dùng).
// Giao diện theo mùa đã bỏ (30/10/2026, user: "bỏ mấy cái giao diện theo mùa đi") — không gọi startSeason(): không có
// data-season, không hỏi /public/season, các thành phần components/season/* không vẽ gì.

// Guard against React 18 StrictMode double-invoking effects and against
// concurrent App remounts during Vite HMR. Both would call tryRestoreSession()
// twice with the same refresh token — the second call trips family-revoke.
let restoreOnce: Promise<boolean> | null = null
function ensureRestore(): Promise<boolean> {
  if (!restoreOnce) restoreOnce = tryRestoreSession()
  return restoreOnce
}

function AdminRedirect() {
  React.useEffect(() => {
    window.location.href = '/admin-console/'
  }, [])
  return (
    <div className="min-h-screen grid place-items-center bg-paper text-muted">
      <div className="flex items-center gap-2 text-sm">
        <svg viewBox="0 0 24 24" className="size-4 animate-spin" aria-hidden>
          <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2.5"
            strokeLinecap="round" strokeDasharray="40 60" />
        </svg>
        Đang chuyển tới Quản trị…
      </div>
    </div>
  )
}

function App() {
  const viewport = useViewportMode()
  const [booting, setBooting] = React.useState(true)
  React.useEffect(() => {
    ensureRestore().finally(() => setBooting(false))
  }, [])

  if (booting) {
    return (
      <div className="min-h-screen grid place-items-center bg-paper text-muted">
        <div className="flex items-center gap-2 text-sm">
          <svg viewBox="0 0 24 24" className="size-4 animate-spin" aria-hidden>
            <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2.5"
              strokeLinecap="round" strokeDasharray="40 60" />
          </svg>
          Đang khôi phục phiên…
        </div>
      </div>
    )
  }

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/2fa" element={<Verify2FaPage />} />
        {/* Điện thoại duyệt đăng nhập cho máy khác — tự báo khi chưa đăng nhập (không đưa sang /login) */}
        <Route path="/qr" element={<React.Suspense fallback={null}><QrScanPage /></React.Suspense>} />

        {/* Không còn trang giới thiệu: "/" và link cũ "/home" chuyển thẳng vào app */}
        <Route path="/" element={<RootRedirect />} />
        <Route path="/home" element={<RootRedirect />} />

        {/* Hoàn tất thông tin tài khoản — bắt buộc trước khi dùng hệ thống (màn riêng, không khung app) */}
        <Route path="/onboarding" element={<RequireAuth><OnboardingPage /></RequireAuth>} />

        {/* Bảng điều khiển nghiệp vụ (Dashboard) yêu cầu đăng nhập. ProfileGate đưa đơn vị
            chưa đủ hồ sơ về /onboarding (trừ Cài đặt, Thông báo, Góp ý). */}
        <Route
          element={
            <RequireAuth>
              <ProfileGate>
                <AppShell />
              </ProfileGate>
            </RequireAuth>
          }
        >
          <Route path="/app" element={<RequireRole path="/app"><OverviewPage /></RequireRole>} />
          <Route path="/pos" element={<Navigate to="/app" replace />} />
          <Route path="/orders" element={<RequireRole path="/orders"><OrdersPage /></RequireRole>} />
          <Route path="/orders/new" element={<RequireRole path="/orders/new"><OrderCreatePage /></RequireRole>} />
          <Route path="/orders/:id" element={<RequireRole path="/orders"><OrderDetailPage /></RequireRole>} />
          <Route path="/ship-payments" element={<RequireRole path="/ship-payments"><ShipPaymentsPage /></RequireRole>} />
          <Route path="/ship-payments/:id" element={<RequireRole path="/ship-payments"><ShipPaymentBatchPage /></RequireRole>} />
          <Route path="/products" element={<RequireRole path="/products"><ProductsPage /></RequireRole>} />
          <Route path="/products/new" element={<RequireRole path="/products/new"><ProductCreatePage /></RequireRole>} />
          <Route path="/products/:id/edit" element={<RequireRole path="/products/edit"><ProductCreatePage /></RequireRole>} />
          <Route path="/customers" element={<RequireRole path="/customers"><CustomersPage /></RequireRole>} />
          <Route path="/rescue" element={<Navigate to="/orders" replace />} />
          {/* Đối soát chỉ đọc cho mọi đơn vị (GĐ9) — thay Ví / rút tiền / đối soát cũ */}
          <Route path="/reconcile" element={<RequireRole path="/reconcile"><ReconcilePage /></RequireRole>} />
          <Route path="/settlements" element={<Navigate to="/reconcile" replace />} />
          <Route path="/wallet" element={<Navigate to="/reconcile" replace />} />
          <Route path="/reports" element={<RequireRole path="/reports"><ReportsPage /></RequireRole>} />
          <Route path="/intake" element={<Navigate to="/orders" replace />} />
          <Route path="/channels" element={<Navigate to="/orders" replace />} />
          <Route path="/tax" element={<Navigate to="/app" replace />} />
          <Route path="/settings" element={<RequireRole path="/settings"><SettingsPage /></RequireRole>} />
          <Route path="/settings/:tab" element={<RequireRole path="/settings"><SettingsPage /></RequireRole>} />
          <Route path="/shipping" element={<RequireRole path="/shipping"><ShippingPage /></RequireRole>} />
          <Route path="/rates" element={<RequireRole path="/rates"><PriceListsPage /></RequireRole>} />
          <Route path="/commissions" element={<RequireRole path="/commissions"><CommissionsRoute ctvView={false} /></RequireRole>} />
          <Route path="/commissions/me" element={<RequireRole path="/commissions/me"><CommissionsRoute ctvView /></RequireRole>} />
          <Route path="/feedback" element={<FeedbackPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          {/* Trang chờ khi đổi đơn vị (components/layout/shell-model.ts switchUnit) */}
          <Route path="/doi-don-vi" element={<UnitSwitching />} />

          {/* Gom Đơn — một trang kiểu Pancake (features/gomdon). Shop chưa mở Gom Đơn vào thẳng thì thấy thông báo, menu không có.
              /comments, /chat-channels: đường cũ, chuyển về /inbox (trang đích tự kiểm quyền) */}
          <Route path="/inbox" element={<RequireRole path="/inbox"><GomdonPage /></RequireRole>} />
          <Route path="/inbox/:id" element={<RequireRole path="/inbox"><GomdonPage /></RequireRole>} />
          <Route path="/comments" element={<GomdonRedirect to="comments" />} />
          <Route path="/comments/:id" element={<GomdonRedirect to="comments" />} />
          <Route path="/chat-channels" element={<GomdonRedirect to="chat-channels" />} />

          {/* Đại lý */}
          <Route path="/agency" element={<RequireRole path="/agency"><AgencyOverview /></RequireRole>} />
          <Route path="/agency/overview" element={<RequireRole path="/agency"><AgencyOverview /></RequireRole>} />
          <Route path="/agency/network" element={<Navigate to="/network" replace />} />
          <Route path="/agency/orders" element={<RequireRole path="/agency"><AgencyOrders /></RequireRole>} />
          {/* Sổ hoa hồng cũ (theo %) đã bỏ — thay bằng Hoa hồng CTV theo số đơn */}
          <Route path="/agency/finance" element={<Navigate to="/commissions" replace />} />
          <Route path="/agency/rates" element={<Navigate to="/rates" replace />} />
          <Route path="/agency/reconcile" element={<Navigate to="/reconcile" replace />} />
          <Route path="/agency/settlements" element={<Navigate to="/reconcile" replace />} />

          {/* Cộng tác viên (CTV) */}
          <Route path="/ctv" element={<RequireRole path="/ctv"><CtvDashboard /></RequireRole>} />
          <Route path="/ctv/dashboard" element={<RequireRole path="/ctv"><CtvDashboard /></RequireRole>} />
          <Route path="/ctv/shops" element={<Navigate to="/network" replace />} />
          <Route path="/ctv/orders" element={<RequireRole path="/ctv"><CtvOrders /></RequireRole>} />
          <Route path="/ctv/wallet" element={<Navigate to="/reconcile" replace />} />
          <Route path="/ctv/rates" element={<Navigate to="/rates" replace />} />
          <Route path="/ctv/reconcile" element={<Navigate to="/reconcile" replace />} />
          <Route path="/ctv/settlements" element={<Navigate to="/reconcile" replace />} />

          {/* Mạng lưới — Đại lý & CTV (GĐ4) */}
          <Route path="/network" element={<RequireRole path="/network"><NetworkPage /></RequireRole>} />
          <Route path="/network/upgrade-requests" element={<RequireRole path="/network"><UpgradeRequestsPage /></RequireRole>} />
          <Route path="/network/announcements" element={<RequireRole path="/network/announcements"><NetworkAnnouncementsPage /></RequireRole>} />
          <Route path="/upgrade" element={<RequireRole path="/upgrade"><UpgradePage /></RequireRole>} />
        </Route>
        {/* Khu Quản trị NPP — cùng khung (AppShell) với đơn vị, menu/màu riêng; không qua ProfileGate */}
        <Route
          element={
            <RequireAuth>
              <NppGate>
                <AppShell />
              </NppGate>
            </RequireAuth>
          }
        >
          {nppRoutes}
        </Route>

        <Route path="/admin" element={<AdminRedirect />} />
        <Route path="/admin/*" element={<AdminRedirect />} />
        <Route path="/admin-console" element={<AdminRedirect />} />
        <Route path="/admin-console/*" element={<AdminRedirect />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster
        // Điện thoại: nổi ở đáy (trên thanh tab) — ở đầu màn nó che thanh trên, gồm công tắc
        // "Quản lý / Bán hàng" của Đại lý / CTV, suốt mấy giây sau mỗi thông báo.
        // Màn ≤600px sonner bỏ qua `offset`, chỉ dùng `mobileOffset` — 156 để nổi trên cả dock lẫn nút Tạo đơn.
        position={viewport === 'mobile' ? 'bottom-center' : 'top-right'}
        offset={viewport === 'mobile' ? 104 : undefined}
        mobileOffset={{ bottom: 156 }}
        richColors
        closeButton
        toastOptions={{
          style: {
            background: 'rgb(var(--raise))',
            color: 'rgb(var(--ink))',
            border: '1px solid rgb(var(--rule))',
          },
        }}
      />
      <ConfirmHost />
      <BrandSync />
    </BrowserRouter>
  )
}

// Thương hiệu NPP nhớ lần cuối — áp ngay từ đầu để không nháy màu ShopTrack (BrandSync chỉnh lại sau); màn đăng nhập
// luôn ShopTrack nên bỏ qua
if (!isAuthScreen(window.location.pathname)) applyBrand(rememberedBrand(), { remember: false })

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
)
